/**
 * NotificationBell — subtle dashboard header bell for in-app reminder notifications.
 * Calm, not social-media style. Shows upcoming reminders and unread notices.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Bell, BellRing, Check, ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';
import { remindersApi, REMINDER_TYPE_ICONS } from '../../utils/reminders';
import { useAuth } from '../../contexts/AuthContext';

export function NotificationBell() {
  const { token, authReady } = useAuth();
  const [unread, setUnread] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);

  const fetchCount = useCallback(async () => {
    try {
      const data = await remindersApi.notifications.unreadCount();
      setUnread(data.unread || 0);
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    if (!authReady || !token) {
      setUnread(0);
      return undefined;
    }
    fetchCount();
    const interval = setInterval(fetchCount, 60_000);
    return () => clearInterval(interval);
  }, [authReady, token, fetchCount]);

  useEffect(() => {
    if (!open) return;
    const fn = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, [open]);

  const handleOpen = async () => {
    setOpen((o) => !o);
    if (!open) {
      setLoading(true);
      try {
        const data = await remindersApi.notifications.list(10);
        setNotifications(data);
      } catch { /* silent */ }
      finally { setLoading(false); }
    }
  };

  const handleMarkAllRead = async () => {
    await remindersApi.notifications.markAllRead().catch(() => {});
    setNotifications((ns) => ns.map((n) => ({ ...n, read: true })));
    setUnread(0);
  };

  const handleReadOne = async (id) => {
    await remindersApi.notifications.markRead(id).catch(() => {});
    setNotifications((ns) => ns.map((n) => n.id === id ? { ...n, read: true } : n));
    setUnread((u) => Math.max(0, u - 1));
  };

  return (
    <div ref={ref} className="relative" data-testid="notification-bell">
      <button
        type="button"
        onClick={handleOpen}
        className="relative flex h-9 w-9 items-center justify-center rounded-full text-[#5c534a] transition hover:bg-[#ebe6dc]/60"
        aria-label={`Notifications${unread > 0 ? ` (${unread} unread)` : ''}`}
      >
        {unread > 0 ? (
          <BellRing className="h-5 w-5" aria-hidden />
        ) : (
          <Bell className="h-5 w-5 opacity-70" aria-hidden />
        )}
        {unread > 0 && (
          <span className="absolute right-1 top-1 flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[9px] font-bold text-primary-foreground">
            {unread > 9 ? '9+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-11 z-50 w-80 rounded-2xl border border-[hsl(38_14%_86%)] bg-[hsl(42_28%_99%)] shadow-[0_16px_40px_-8px_rgba(28,25,23,0.15)]"
          role="dialog"
          aria-label="Notifications"
        >
          <div className="flex items-center justify-between border-b border-[hsl(38_14%_88%)] px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              Reminders
            </p>
            <div className="flex items-center gap-3">
              {unread > 0 && (
                <button
                  type="button"
                  onClick={handleMarkAllRead}
                  className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Check className="h-3 w-3" /> Mark all read
                </button>
              )}
              <Link to="/reminders" onClick={() => setOpen(false)} className="text-[11px] text-primary hover:underline">
                View all
              </Link>
            </div>
          </div>

          <div className="max-h-80 overflow-y-auto py-1">
            {loading && (
              <p className="px-4 py-6 text-center text-sm text-muted-foreground">Loading…</p>
            )}
            {!loading && notifications.length === 0 && (
              <div className="px-4 py-6 text-center">
                <Bell className="mx-auto mb-2 h-6 w-6 text-muted-foreground/40" />
                <p className="text-sm text-muted-foreground/70">You&apos;re all caught up.</p>
                <Link to="/reminders" onClick={() => setOpen(false)} className="mt-1 block text-xs text-primary hover:underline">
                  Set up reminders
                </Link>
              </div>
            )}
            {!loading && notifications.map((n) => (
              <div
                key={n.id}
                className={`flex items-start gap-3 px-4 py-3 transition-colors hover:bg-muted/30 ${!n.read ? 'bg-primary/3' : ''}`}
                onClick={() => !n.read && handleReadOne(n.id)}
                role="button"
                tabIndex={0}
              >
                <span className="mt-0.5 text-base leading-none">
                  {REMINDER_TYPE_ICONS[n.notification_type] || '📅'}
                </span>
                <div className="min-w-0 flex-1">
                  <p className={`text-sm leading-snug ${!n.read ? 'font-medium text-foreground' : 'text-foreground/80'}`}>
                    {n.title}
                  </p>
                  <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground/80 line-clamp-2">
                    {n.message}
                  </p>
                </div>
                {!n.read && (
                  <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />
                )}
              </div>
            ))}
          </div>

          <div className="border-t border-[hsl(38_14%_88%)] px-4 py-2.5">
            <Link
              to="/reminders"
              onClick={() => setOpen(false)}
              className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              Manage reminders & tribute cards
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}

export default NotificationBell;
